/**
 * useLicensingNotifications
 *
 * Polls GET /api/licensing/notifications and surfaces active license/credit
 * usage notifications (80/95% used, license expiring/expired) via the global
 * NotificationContext. CREDITS_100 is intentionally excluded — it's already
 * shown as the non-dismissible "AI Credits depleted" banner at the top of the
 * app (TrialBanner.tsx), so surfacing it here too would duplicate the same
 * message. Non-dismissible notifications (e.g. LICENSE_EXPIRED) have no close
 * button and never auto-dismiss (see NotificationPortal.tsx).
 *
 * ADMIN-audience notifications are already filtered server-side, but we
 * re-check `isAdmin` client-side as a defense-in-depth layer.
 */
import { useEffect, useRef } from 'react';
import { api } from '../services/api';
import { useNotify } from '../contexts/NotificationContext';
import type { Notification, NotificationType } from '../types';
import { withJitter, isTabHidden } from '../utils/polling';

const POLL_INTERVAL_MS = 60_000;

const SEVERITY_TO_TYPE: Record<string, NotificationType> = {
  INFO: 'info',
  WARNING: 'warning',
  CRITICAL: 'error',
};

export function useLicensingNotifications(enabled: boolean, isAdmin: boolean): void {
  const { notifications, addNotification, removeNotification } = useNotify();

  const notificationsRef = useRef<Notification[]>(notifications);
  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  const shownIds = useRef<Set<string>>(new Set());
  const locallyDismissed = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    const poll = async () => {
      // Ambient poller — skip while backgrounded so it stops generating
      // load against the backend when nobody's looking.
      if (isTabHidden()) return;

      // Detect toasts the user closed manually since the last poll and persist the dismissal
      // so a re-poll (server condition still active) doesn't immediately bring it back.
      for (const id of Array.from(shownIds.current)) {
        const stillVisible = notificationsRef.current.some(n => n.id === id);
        if (!stillVisible && !locallyDismissed.current.has(id)) {
          locallyDismissed.current.add(id);
          shownIds.current.delete(id);
          api.licensing.dismissNotification(id).catch(e => {
            // Non-fatal: the toast is already closed locally. Log so a
            // recurring 403/404/422 from admin.py's dismiss endpoint is
            // diagnosable instead of silently swallowed.
            console.warn('[useLicensingNotifications] dismiss failed:', id, e);
          });
        }
      }

      try {
        const items = await api.licensing.getNotifications();
        if (cancelled) return;

        for (const n of items) {
          if (locallyDismissed.current.has(n.id)) continue;
          if (n.audience === 'ADMIN' && !isAdmin) continue;
          // CREDITS_100 is already surfaced as the non-dismissible "AI Credits
          // depleted" banner at the top of the app (TrialBanner, driven by the
          // same credits_balance <= 0 condition) — skip it here so the two
          // don't both show the same "100% used" message at once.
          if (n.code === 'CREDITS_100') continue;
          if (shownIds.current.has(n.id)) continue;
          addNotification(
            SEVERITY_TO_TYPE[n.severity] ?? 'warning',
            n.message,
            undefined,
            { id: n.id, dismissible: n.dismissible, duration: n.dismissible ? 10000 : 0 },
          );
          shownIds.current.add(n.id);
        }

        // Condition resolved server-side (e.g. balance topped up) — drop the toast.
        const activeIds = new Set(items.map(n => n.id));
        for (const id of Array.from(shownIds.current)) {
          if (!activeIds.has(id)) {
            removeNotification(id);
            shownIds.current.delete(id);
          }
        }
      } catch (e) {
        // Non-fatal: swallowed and retried on the next interval, so this is
        // a warning rather than an error (E2E ConsoleErrorCollector treats
        // console.error as a test failure).
        console.warn('[useLicensingNotifications] poll failed:', e);
      }
    };

    void poll();
    const interval = setInterval(poll, withJitter(POLL_INTERVAL_MS));
    const onVisible = () => { if (document.visibilityState === 'visible') void poll(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // addNotification/removeNotification are stable (useCallback in useNotifications);
    // notifications is read via notificationsRef so it's intentionally excluded here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, isAdmin]);
}
